import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as BackSquareIcon } from '../../../assets/icons/Back-Square-icon.svg';
import { ReactComponent as DownArrowIcon } from '../../../assets/icons/down-arrow.svg';
import { PortalType } from '../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { fetchAllPermissions, fetchAllRoles } from '../../../redux/slices/RolesManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomCheckbox from '../../../shared-components/custom-check-box/CustomCheckBox';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import TabToggle, { TabItem } from '../../../shared-components/tab-toggle/TabToggle';
import { getUserType, platformPermissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import roleApi from '../../../utils/api/roles';
import useLocalizedPermissions from '../hooks/useLocalizedPermissions';

interface UserDetails {
  name: string;
  responsibility: string;
}

/**
 * Checks if a specific permission tab is allowed for the logged in user based on platform permissions.
 * @param {'Network' | 'Mobility' | 'Roaming' | 'Devices' | 'Users'} tab - The tab name to check.
 * @returns {boolean} True if the tab is permitted.
 */
const isTabAllowed = (
  tab: 'Network' | 'Mobility' | 'Roaming' | 'Devices' | 'Users',
): boolean => {
  const userType = getUserType();
  if (userType === PortalType.APP) {
    if (tab === 'Network') return platformPermissionCheck(PortalType.NETWORKS);
    if (tab === 'Mobility') return platformPermissionCheck(PortalType.MOBILITY);
    if (tab === 'Roaming') return platformPermissionCheck(PortalType.ROAMING);
    if (tab === 'Devices') return platformPermissionCheck(PortalType.CHARGER);
    if (tab === 'Users') return platformPermissionCheck(PortalType.USERS);
  } else {
    if (tab === 'Network') return userType === PortalType.NETWORKS;
    if (tab === 'Mobility') return userType === PortalType.MOBILITY;
    if (tab === 'Roaming') return userType === PortalType.ROAMING;
    if (tab === 'Devices') return userType === PortalType.CHARGER;
    if (tab === 'Users') return platformPermissionCheck(PortalType.USERS);
  }
  return true;
};

/**
 * Maps a permission group key or name to the corresponding tab.
 * @param {string} groupKey - The unique key identifying the permission group.
 * @param {string} groupName - The user-friendly name of the permission group.
 * @returns {'Network' | 'Mobility' | 'Devices' | 'Roaming' | 'Users'} The name of the tab matching the group.
 */
const getGroupTab = (
  groupKey: string,
  groupName: string,
): 'Network' | 'Mobility' | 'Devices' | 'Roaming' | 'Users' => {
  const normalizedKey = (groupKey || '').toLowerCase();
  const normalizedName = (groupName || '').toLowerCase();

  if (
    normalizedKey.includes('cpo') ||
    normalizedName.includes('cpo') ||
    normalizedKey.includes('network') ||
    normalizedName.includes('network')
  ) {
    return 'Network';
  }
  if (
    normalizedKey.includes('emsp') ||
    normalizedName.includes('emsp') ||
    normalizedKey.includes('mobility') ||
    normalizedName.includes('mobility')
  ) {
    return 'Mobility';
  }
  if (normalizedKey.includes('roaming') || normalizedName.includes('roaming')) {
    return 'Roaming';
  }
  if (
    normalizedKey.includes('device') ||
    normalizedName.includes('device') ||
    normalizedKey.includes('charger') ||
    normalizedName.includes('charger')
  ) {
    return 'Devices';
  }
  return 'Users';
};

/**
 * Add Role Page component
 * @returns {React.ReactElement} Add role page component
 */
function AddRolePage(): JSX.Element {
  const { t, i18n } = useTranslation('userRole');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { permissionsList, loading } = useAppSelector((state) => state.roles);
  const localizedPermissionsList = useLocalizedPermissions(permissionsList);

  const [userDetails, SetUserDetails] = useState<UserDetails>({
    name: '',
    responsibility: '',
  });

  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');

  const [modalBoxDetail, setModalBoxDetail] = useState<any>({});

  const [showFormErrors, setShowFormErrors] = useState(false);
  const [expandedModules, setExpandedModules] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<
    'Network' | 'Mobility' | 'Roaming' | 'Devices' | 'Users'
  >('Network');

  const allTabs: Array<'Network' | 'Mobility' | 'Roaming' | 'Devices' | 'Users'> = [
    'Network',
    'Mobility',
    'Roaming',
    'Devices',
    'Users',
  ];
  const allowedTabs = allTabs.filter(isTabAllowed);

  useEffect(() => {
    if (allowedTabs.length > 0 && !allowedTabs.includes(activeTab)) {
      setActiveTab(allowedTabs[0]);
    }
  }, [allowedTabs]);
  /**
   * BACK
   */
  const handleBackClick = () => {
    navigate('../role');
  };

  /**
   * INPUT CHANGE
   * @param {string} name - The name of the input
   * @param {any} value - The value of the input
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    SetUserDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * SINGLE SELECT
   * @param {string} value - The permission to toggle
   */
  const handlePermissionSelect = (value: string) => {
    setSelectedPermissions((previous) =>
      previous.includes(value) ? previous.filter((item) => item !== value) : [...previous, value],
    );
  };

  /**
   * RESET
   */
  const resetRoleForm = () => {
    SetUserDetails({
      name: '',
      responsibility: '',
    });

    setSelectedPermissions([]);
  };

  /**
   * toggleExpand
   * @param {string} moduleKey - The key of the module
   */
  const toggleExpand = (moduleKey: string) => {
    setExpandedModules((previous: string[]) =>
      previous.includes(moduleKey)
        ? previous.filter((item: string) => item !== moduleKey)
        : [...previous, moduleKey],
    );
  };

  /**
   * SUBMIT
   */
  const handleSubmitRole = () => {
    setShowFormErrors(true);

    if (
      !userDetails.name.trim() ||
      !userDetails.responsibility.trim() ||
      selectedPermissions.length === 0
    ) {
      return;
    }
    const hasPermission = permissionsList
      .map((permission) => {
        const permissionKeys = permission.permissions.flatMap((module: any) =>
          module.features.map((future: any) => future.permission),
        );
        return permissionKeys.some((perm: string) => selectedPermissions.includes(perm))
          ? permission.key
          : null;
      })
      .filter((v): v is string => !!v);

    const payload = {
      name: userDetails.name,
      responsibility: userDetails.responsibility,
      permissions: selectedPermissions,
      platformPermissions: hasPermission,
    };
    roleApi
      .createRoles(payload)
      .then(() => {
        setModalBoxStatus('open');

        setModalBoxDetail({
          title: '',
          rightCorner: '',
          notification: true,
          content: (
            <SuccessNotification
              title={t('roleAddedSuccessfully')}
              description={t('roleAddedSuccessfully')}
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                dispatch(fetchAllRoles({ page: 1, limit: 10 }));
                resetRoleForm();
                navigate('../role');
              }}
            />
          ),
        });
      })
      .catch((error) => {
        setModalBoxStatus('close');
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  const activeTabGroups = localizedPermissionsList.filter(
    (group: any) => getGroupTab(group.key, group.name) === activeTab,
  );
  const activeKeys = activeTabGroups.flatMap((group: any) => [
    group.key,
    ...group.permissions.map((permission: any) => permission.key),
  ]);
  const isExpandedAll =
    activeKeys.length > 0 && activeKeys.every((key) => expandedModules.includes(key));

  const activeTabPermissions = activeTabGroups.flatMap((group: any) =>
    group.permissions.flatMap((module: any) =>
      module.features.map((permission: any) => permission.permission),
    ),
  );
  const isActiveTabAllSelected =
    activeTabPermissions.length > 0 &&
    activeTabPermissions.every((perm: string) => selectedPermissions.includes(perm));

  /**
   * Toggles all permissions within the active tab.
   * @param {boolean} checked - True to select all permissions in the tab, false to deselect.
   * @returns {void}
   */
  const toggleAllowActiveTab = (checked: boolean) => {
    if (checked) {
      setSelectedPermissions((previous) => [...new Set([...previous, ...activeTabPermissions])]);
    } else {
      setSelectedPermissions((previous) =>
        previous.filter((perm) => !activeTabPermissions.includes(perm)),
      );
    }
  };

  /**
   * Handle the change of active tab
   * @param {string} tab - The selected tab value
   */
  const handleTabChange = (tab: string) => {
    setActiveTab(tab as 'Network' | 'Mobility' | 'Roaming' | 'Devices' | 'Users');
  };

  /**
   * Handles expand/collapse all for permission modules under the active tab.
   * @returns {void}
   */
  const handleExpandAll = () => {
    if (isExpandedAll) {
      setExpandedModules((previous: string[]) =>
        previous.filter((key: string) => !activeKeys.includes(key)),
      );
    } else {
      setExpandedModules((previous: string[]) => [...new Set([...previous, ...activeKeys])]);
    }
  };

  useEffect(() => {
    if (permissionsList.length > 0) {
      setExpandedModules(permissionsList.map((group: any) => group.key));
    }
  }, [permissionsList]);

  useEffect(() => {
    if (!loading && permissionsList.length === 0) {
      dispatch(fetchAllPermissions());
    }
  }, []);

  const tabLabelMap: Record<'Network' | 'Mobility' | 'Roaming' | 'Devices' | 'Users', string> = {
    Network: t('network'),
    Mobility: t('mobility'),
    Roaming: t('roaming'),
    Devices: t('devices'),
    Users: t('users'),
  };

  const tabItems: TabItem[] = allowedTabs.map((tab) => ({
    label: tabLabelMap[tab] || tab,
    value: tab,
  }));

  return (
    <div
      className={`add-role-page ${i18n.language === 'ar' ? 'rtl' : ''}`}
      dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        rightCorner={modalBoxDetail.rightCorner}
        notification={modalBoxDetail.notification}
        header={!modalBoxDetail.notification}
      />

      {/* HEADER */}
      <div className="page-header">
        <div className={`back-button ${i18n.language === 'ar' ? 'rtl' : ''}`}>
          <span role="presentation" onClick={handleBackClick}>
            <BackSquareIcon />
          </span>
        </div>

        <p>{t('roleAndPermissions')}</p>

        <div className="button-container">
          <MyButton label={t('back')} buttonType="primary-outline" onClickFunc={handleBackClick} />

          <MyButton label={t('submit')} buttonType="submit" onClickFunc={handleSubmitRole} />
        </div>
      </div>

      {/* CARD */}
      <div className="add-user-role-card">
        <div className="card-header">
          <h4>{t('addRole')}</h4>

          <p>{t('rolePermissionDescription')}</p>
        </div>

        <div className="card-inputs">
          <CustomInput
            name="name"
            inputLabel={t('role')}
            placeholder={t('enterRole')}
            value={userDetails.name}
            onChangeValue={handleInputChange}
            type="text"
            valueRequired
            error={showFormErrors && !userDetails.name.trim()}
            errorMessage={t('roleRequired')}
          />

          <CustomInput
            name="responsibility"
            inputLabel={t('responsibility')}
            placeholder={t('describeHere')}
            value={userDetails.responsibility}
            onChangeValue={handleInputChange}
            type="text"
            valueRequired
            error={showFormErrors && !userDetails.responsibility.trim()}
            errorMessage={t('responsibilityRequired')}
          />
        </div>
      </div>

      {/* PERMISSIONS */}
      {!loading && permissionsList.length > 0 && (
        <div className="permissions-section">
          {/* HEADER */}
          <div className="permissions-header">
            <div className="permissions-tab-toggle">
              {allowedTabs.length > 0 && (
                <TabToggle
                  tabs={tabItems}
                  defaultTab={allowedTabs.includes(activeTab) ? activeTab : allowedTabs[0]}
                  activeTab={activeTab}
                  isLabelValue
                  onChange={handleTabChange}
                />
              )}
            </div>

            <div className="permissions-actions">
              <div className="expand-all" role="presentation" onClick={handleExpandAll}>
                <span>{isExpandedAll ? t('collapseAll') : t('expandAll')}</span>
                <button
                  type="button"
                  className={`expand-btn ${isExpandedAll ? 'open' : ''}`}
                  aria-label={t('togglePermissions')}>
                  <DownArrowIcon />
                </button>
              </div>

              <div className="allow-all-toggle">
                <CustomCheckbox
                  name="all"
                  value={isActiveTabAllSelected}
                  checkboxLabel={t('allowAllPermissions')}
                  onChangeValue={() => toggleAllowActiveTab(!isActiveTabAllSelected)}
                />
              </div>
            </div>
          </div>

          {/* GROUPS */}
          {localizedPermissionsList
            .filter((group: any) => getGroupTab(group.key, group.name) === activeTab)
            .map((group: any) => {
              return (
                <div key={group.key} className="permission-group-card">
                  {/* MODULES - DEFAULT CLOSED */}
                  {expandedModules.includes(group.key) && (
                    <div className="group-module-list">
                      {group.permissions.map((module: any) => {
                        const moduleSelected = module.features.every((permission: any) =>
                          selectedPermissions.includes(permission.permission),
                        );
                        return (
                          <div key={module.key} className="module-card">
                            {/* MODULE HEADER */}
                            <div
                              className="module-header"
                              onClick={() => toggleExpand(module.key)}
                              role="presentation">
                              <span>{module.localizedName || module.name}</span>

                              <div className="module-header-right">
                                <div>
                                  <CustomCheckbox
                                    name={module.key}
                                    value={moduleSelected}
                                    checkboxLabel={t('selectAll')}
                                    onChangeValue={() => {
                                      const modulePermissions = module.features.map(
                                        (permission: any) => permission.permission,
                                      );
                                      const allSelected = modulePermissions.every(
                                        (permission: any) =>
                                          selectedPermissions.includes(permission),
                                      );
                                      if (allSelected) {
                                        // Deselect all permissions in the module
                                        modulePermissions.forEach((permission: any) => {
                                          handlePermissionSelect(permission);
                                        });
                                      } else {
                                        // Select all permissions in the module
                                        modulePermissions.forEach((permission: any) => {
                                          handlePermissionSelect(permission);
                                        });
                                      }
                                    }}
                                  />
                                </div>

                                {/* MODULE DROPDOWN BUTTON */}
                                <button
                                  type="button"
                                  className={`expand-btn ${
                                    expandedModules.includes(module.key) ? 'open' : ''
                                  }`}
                                  aria-label={t('togglePermissions')}>
                                  <DownArrowIcon />
                                </button>
                              </div>
                            </div>

                            {/* PERMISSIONS */}
                            {expandedModules.includes(module.key) && (
                              <div className="permission-list">
                                {module.features.map((permission: any) => {
                                  return (
                                    <CustomCheckbox
                                      key={permission.permission}
                                      name={permission.permission}
                                      value={selectedPermissions.includes(permission.permission)}
                                      checkboxLabel={permission.localizedName || permission.name}
                                      onChangeValue={() =>
                                        handlePermissionSelect(permission.permission)
                                      }
                                    />
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

export default AddRolePage;
